import { useEffect } from "react";

import { BASE_API_URL } from "@/constants";
import * as Sentry from "@sentry/react";
import { ViewerKindSchema, ViewerFileType } from "./fileViewerInterface";
import { parseContentDispositionFilename } from "./parseContentDispositionFilename";
import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";

const useFileLoader = (
  fileId: string | null,
  fileType: ViewerFileType | null | undefined,
  loadAnyways: boolean,
  /** Where to read the bytes from when they don't live behind a blob id — a
   *  file a chat turn produced is addressed by turn and filename (#10988). */
  previewUrl?: string | null
) => {
  fileId ||= "";
  if (fileId.includes("test-")) {
    return {
      fileType: fileType,
      response: undefined,
      fileUrl: fileId.split("test-")[1],
      error: null,
      isLoading: false,
      unrecognizedKind: null,
      isLocked: false,
    };
  }
  // A blob id always wins. Most callers merge into the viewer's state rather
  // than replacing it, so a previously-opened chat output's `previewUrl` can
  // still be sitting there when a real document is opened — and it must not
  // shadow it.
  const fileUrl =
    fileId || !previewUrl
      ? `${BASE_API_URL}/userData/fp0065?fileId=${fileId}&v=cacheBust9`
      : previewUrl;

  // Load the file if necessary
  const { data, error, isLoading } = useQuery({
    queryKey: ["fp0065", fileUrl],
    queryFn: async () => {
      const response = await fpFetch(fileUrl);

      // Check if the response is ok before processing
      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => "Failed to load file");
        throw new Error(
          `Failed to load file: ${response.status} ${response.statusText}. ${errorText}`
        );
      }

      const contentType = response.headers.get("Content-Type") || "";
      const contentDisposition =
        response.headers.get("Content-Disposition") || "";
      const payload = await response.arrayBuffer();
      return new ResponseWrapper(payload, contentType, contentDisposition);
    },
    enabled: Boolean(fileId || previewUrl) && loadAnyways,
    refetchOnWindowFocus: false, // Prevent refetch when tab regains focus
    refetchOnReconnect: false, // Prevent refetch on reconnect
    // A refetch (a reopen reads the file again, so a file that changed under
    // the same URL shows its new bytes) keeps the result it already had
    // when the bytes are the same: the viewer keeps the document it drew
    // rather than loading it a second time (#13261, Hunter: "loads, then
    // reloads itself"). Different bytes are a new result, and a new load.
    structuralSharing: (previous: unknown, next: unknown) =>
      previous instanceof ResponseWrapper &&
      next instanceof ResponseWrapper &&
      sameFile(previous, next)
        ? previous
        : next,
  });

  // Extract the file type from the headers
  let localFileType: ViewerFileType | null = null;
  // The kind the endpoint declared, when we could not name it. Distinct
  // from 'no kind yet' (still loading) -- see the early-return shape below.
  let unrecognizedKind: string | null = null;
  // The endpoint declares a still-locked document beside the kind
  // (`; locked=1`), so the viewer can show its password panel instead of
  // handing encrypted bytes to a renderer (#11856).
  let isLocked = false;
  if (data) {
    // Extracting fileType from Content-Type because of CORS' mental issues
    const contentType = data.contentType;
    let fileType = null;
    if (contentType) {
      // Content-Type: application/pdf; fileType=pdf; locked=1
      const parameters = contentTypeParameters(contentType);
      fileType = parameters.get("fileType") ?? null;
      isLocked = parameters.get("locked") === "1";
    }

    // Only parse if we have a valid fileType
    if (fileType) {
      const parsed = ViewerKindSchema.safeParse(fileType);
      if (parsed.success) {
        localFileType = parsed.data;
      } else {
        // A kind the frontend cannot name means the two ends of the wire have
        // drifted. This used to be a console.warn, which meant it degraded into
        // a generic "Unable to preview this file" and nobody found out: #11328
        // reached ~438,000 blobs across every client org before a client
        // reported it, and #11712 sat in production for four weeks. Surface it
        // as its own state; the report is fired from an effect below (#11712).
        unrecognizedKind = fileType;
      }
    }
  }

  // Reported from an effect, not from the render body: the body re-runs on
  // every re-render (zoom, parent state, StrictMode's double render), which
  // would report the same drift dozens of times per file. Keyed on the file and
  // the kind so one occurrence reports once.
  const reportedContentType = data?.contentType;
  useEffect(() => {
    if (!unrecognizedKind) return;
    Sentry.captureException(
      new Error(
        `Unrecognized viewer kind from preview endpoint: ${unrecognizedKind}`
      ),
      {
        tags: { viewerKind: unrecognizedKind },
        extra: { fileId, contentType: reportedContentType },
      }
    );
  }, [unrecognizedKind, fileId, reportedContentType]);

  const fileName: string | null = data
    ? parseContentDispositionFilename(data.contentDisposition)
    : null;

  return {
    fileType: localFileType || fileType,
    unrecognizedKind,
    isLocked,
    response: data,
    fileUrl,
    error,
    fileName,
    isLoading,
  };
};

/** The `key=value` parameters after the media type of a Content-Type. */
const contentTypeParameters = (contentType: string): Map<string, string> => {
  const parameters = new Map<string, string>();
  for (const part of contentType.split(";").slice(1)) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    const key = part.slice(0, separator).trim();
    const value = part.slice(separator + 1).trim();
    if (key && value) parameters.set(key, value);
  }
  return parameters;
};

export default useFileLoader;

/** Whether two reads of a file carry the same bytes under the same type. */
const sameFile = (a: ResponseWrapper, b: ResponseWrapper): boolean => {
  if (
    a.contentType !== b.contentType ||
    a.contentDisposition !== b.contentDisposition ||
    a.payload.byteLength !== b.payload.byteLength
  ) {
    return false;
  }
  const left = new Uint8Array(a.payload);
  const right = new Uint8Array(b.payload);
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
};

export class ResponseWrapper {
  constructor(
    public readonly payload: ArrayBuffer,
    public readonly contentType: string,
    public readonly contentDisposition?: string
  ) {}

  async json() {
    const decoder = new TextDecoder("utf-8");
    const text = decoder.decode(this.payload);
    return JSON.parse(text);
  }

  async text() {
    const decoder = new TextDecoder("utf-8");
    const text = decoder.decode(this.payload);
    return text;
  }

  async blob() {
    const mimeType = this.contentType ? this.contentType.split(";")[0] : "";
    return new Blob([this.payload], { type: mimeType });
  }

  // Didn't know this method existed on `Response`s... maybe this entire class is unnecessary
  clone() {
    return this;
  }
}
